<template>
  <div class="p-3 pb-24 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Game Guide Notification -->
    <div v-if="user && hasCharacter && characterLevel < 10 && !dismissedGameGuide" class="relative text-center text-xs sm:text-sm text-white mb-3 bg-blue-900 bg-opacity-80 rounded-lg p-2 border border-blue-700">
      <div class="font-bold">คำแนะนำสำหรับผู้เล่นใหม่</div>
      <div>
        ผู้เล่นควรเล่นตามภารกิจที่เกมกำหนดไว้ โดยไม่ควรข้ามขั้นตอน เพื่อศึกษาและทำความเข้าใจวิธีการเล่นเกมให้เข้าใจมากยิ่งขึ้น
        <span class="underline hover:text-yellow-300 cursor-pointer" @click="dismissGameGuide">
          ปิดข้อความนี้
        </span>
      </div>
    </div>

    <!-- First Death Notification -->
    <div v-if="user && hasCharacter && death === 1 && !dismissedDeathNotification" class="relative text-center text-xs sm:text-sm text-white mb-3 bg-red-900 bg-opacity-80 rounded-lg p-2 border border-red-700">
      <div class="font-bold">คุณตายครั้งแรก!</div>
      <div>
        นี่คือการตายครั้งแรกของคุณ คุณสามารถเลือกได้ว่าจะเล่นต่อหรือสร้างตัวละครใหม่
        <div class="mt-2 flex justify-center gap-2">
          <router-link :to="{ name: 'Account' }" class="underline hover:text-yellow-300 cursor-pointer">
            สร้างตัวใหม่
          </router-link>
          <span class="underline hover:text-yellow-300 cursor-pointer" @click="dismissDeathNotification">
            เล่นต่อ
          </span>
        </div>
      </div>
    </div>

    <!-- Sell Item Notification -->
    <div v-if="user && hasCharacter && showSellItemNotification" class="text-center text-xs sm:text-sm text-white mb-3 bg-blue-900 bg-opacity-80 rounded-lg p-2 border border-blue-700">
      <div>คุณสามารถนำไอเทมที่คุณหามาได้จากสถานที่ต่างๆขายได้ที่ปุ่มกระเป๋าและเลือกไปที่ขาย</div>
    </div>
 
    <!-- Occupied Outposts Section -->
    <div v-if="user && occupiedMaps.length > 0 || occupiedOutposts.length > 0 " class="grid grid-cols-2 sm:grid-cols-5  gap-1 my-2">
      <div v-for="map in occupiedMaps" :key="map.id" class="">
          <div class="flex justify-between items-center">
            <button
              @click="openTreasure(map)"
              :disabled="isOpeningTreasure || map.treasureOpened || isMapInCooldown(map)"
              class="px-2 py-1 rounded-md text-xs font-medium transition-colors duration-200 focus:outline-none border w-full"
              :class="getMapButtonClasses(map)"
            >
              <i v-if="isOpeningTreasure" class="fas fa-spinner animate-spin text-yellow-300 mr-1"></i>
              <i v-else-if="map.treasureOpened" class="fas fa-lock text-yellow-300 mr-1"></i>
              <i v-else-if="isMapInCooldown(map)" class="fas fa-clock text-yellow-300 mr-1"></i>
              <i v-else class="fas fa-treasure-chest text-yellow-300 mr-1"></i>
              <span>{{ map.name }}</span>
              <span v-if="isMapInCooldown(map)" class="ml-1 text-xs">({{ getMapCooldownText(map) }})</span>
            </button>
          </div>
        </div>
      <div v-for="outpost in occupiedOutposts" :key="outpost.id" class="">
        <button
          @click="openOutpostTreasure(outpost)"
          :disabled="isOpeningTreasure || outpost.treasureOpened || isOutpostInCooldown(outpost)"
          class="px-2 py-1 rounded-md text-xs font-medium transition-colors duration-200 focus:outline-none border w-full flex justify-between items-center"
          :class="getOutpostButtonClasses(outpost)"
        >
          <span class="flex items-center">
            <i v-if="isOpeningTreasure" class="fas fa-spinner animate-spin text-yellow-300 mr-1"></i>
            <i v-else-if="outpost.treasureOpened" class="fas fa-lock text-yellow-300 mr-1"></i>
            <i v-else-if="isOutpostInCooldown(outpost)" class="fas fa-clock text-yellow-300 mr-1"></i>
            <i v-else class="fas fa-treasure-chest text-yellow-300 mr-1"></i>
            <span>{{ outpost.name }}</span>
            <span v-if="isOutpostInCooldown(outpost)" class="ml-1 text-xs">({{ getOutpostCooldownText(outpost) }})</span>
          </span>
          <span
            class="manage-icon p-1 rounded hover:bg-gray-700 border border-transparent hover:border-gray-600 transition-colors ml-1"
            aria-label="Manage outpost"
            @click.stop="openManage(outpost.id)"
            :class="{ 'pointer-events-none opacity-50': isOpeningTreasure }"
          >
            <i class="fa-solid fa-gear text-xs text-gray-300"></i>
          </span>
        </button>
      </div>
    </div>
    <div>
      <!-- Button Grid -->
      <div v-if="!isInitialLoading" class="grid grid-cols-3 sm:grid-cols-5 items-center gap-2 sm:gap-3 text-xs sm:text-sm">
        <!-- ปุ่มล็อกอิน -->
        <router-link v-if="!user" :to="{ name: 'Home' }" custom class="col-span-3 sm:col-span-4">
          <div @click="signInWithGoogle" class="button-wrapper">
            <div class="btn btn-primary btn-google">
              <div v-if="!isLoading" class="btn-content">
                <i class="fab fa-google text-lg"></i>
                <span>Log in With Google</span>
              </div>
              <div v-else class="btn-content">
                <i class="fab fa-google text-lg animate-spin"></i>
              </div>
            </div>
          </div>
        </router-link>

        <!-- ปุ่มสร้างตัวละคร -->
        <router-link v-if="user && !hasCharacter" :to="{ name: 'CreateCharacter' }" class="button-wrapper">
          <div class="btn btn-primary btn-google">
            <div class="btn-content">
              <i class="fas fa-user-plus text-lg"></i>
              <span>สร้างตัวละคร</span>
            </div>
          </div>
        </router-link>

        <!-- ปุ่มที่ถูกล็อคถ้าไม่ได้ล็อกอินหรือไม่มีตัวละคร -->
        <div
          class="button-wrapper"
          :title="user && hasCharacter ? '' : 'ต้องเข้าสู่ระบบและสร้างตัวละครเพื่อใช้งาน'"
          v-for="item in lockedButtons"
          :key="item.name"
        >
          <router-link v-if="user && hasCharacter" :to="{ name: item.route }" class="button-wrapper">
            <div class="btn btn-primary">
              <div class="btn-content relative">
                <i :class="item.icon + ' text-lg'"></i>
                <span>{{ item.label }}</span>

                <!-- Badge: คลังปริศนา -->
                <span
                  v-if="item.name === 'MysteriousStorage' && mysteriousStorageCount > 0"
                  class="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                >
                  {{ mysteriousStorageCount }}
                </span>

                <!-- Badge: กล่องฟรี -->
                <span
                  v-if="item.name === 'MysteriousStorage' && freeBoxCount > 0"
                  class="absolute -top-2 -left-2 text-red-500 animate-pulse text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                  title="คุณสามารถเปิดกล่องปริศนาได้ 1 ครั้ง"
                >
                  <i class="fas fa-gift text-xs"></i>
                </span>

                <!-- Badge: ฝากเพชรได้อีก -->
                <span
                  v-if="item.name === 'Storage' && weeklyDepositedDiamond < 100"
                  class="absolute -top-2 -left-2 text-blue-500 animate-pulse text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                  title="คุณสามารถฝากเพชรได้อีก"
                >
                  <i class="fas fa-gem text-xs"></i>
                </span>

                <!-- Badge: ป้องกันลอบสังหาร -->
                <span
                  v-if="item.name === 'Character' && protectAssassinateRemaining > 0"
                  class="absolute -top-2 -left-2 text-blue-500 animate-pulse text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                  title="การป้องกันการลอบสังหารทำงานอยู่"
                >
                  <i class="fas fa-shield-alt text-xs"></i>
                </span>
                <span
                  v-if="item.name === 'Character' && protectAssassinateRemaining > 0"
                  class="absolute -top-2 -right-2 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                  :title="`การป้องกันการลอบสังหาร: เหลือ ${protectAssassinateRemaining} วินาที`"
                >
                  {{ protectAssassinateRemaining }}
                </span>

                <!-- Badge: activeShops ที่ปุ่ม Marketplace -->
                <span
                  v-if="item.name === 'Marketplace' && activeShops > 0"
                  class="absolute -top-3 -right-3  bg-yellow-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-yellow-500 "
                  :title="`${activeShops} ร้านค้าที่กำลังเปิดอยู่`"
                >
                  {{ activeShops }}
                </span>

                <!-- Badge: ประกัน (โล่ + HH:MM) บนปุ่มผู้จัดการ -->
                <span
                  v-if="item.name === 'ManagerRoom' && insuranceRemainingSeconds > 0"
                  class="absolute -top-2 -left-2 bg-gray-900 bg-opacity-80 border border-indigo-500 text-indigo-200 px-2 py-0.5 rounded-md text-[10px] font-semibold flex items-center gap-1 shadow"
                >
                  <i class="fas fa-shield-alt text-xs text-blue-400"></i>
                  {{ insuranceHoursMinutes }}
                </span>
              </div>
            </div>
          </router-link>

          <div v-else class="btn btn-locked" :disabled="true">
            <div class="btn-content relative">
              <i :class="item.icon + ' text-lg opacity-50'"></i>
              <span class="opacity-50">{{ item.label }}</span>
              <i class="fas fa-lock text-sm absolute top-1 right-1 text-gray-400"></i>
            </div>
          </div>
        </div>

        <!-- ผู้รอดชีวิต -->
        <div v-if="!isLoadingSettings" class="flex items-center justify-center bg-gray-900 bg-opacity-80 rounded-xl shadow-2xl h-full survivor-bg">
          <div>
            <p class="text-base sm:text-lg text-center font-bold space-x-1" :style="{ color: activePlayerColor }">
              {{ activePlayerStatus }}
            </p>
            <h2 class="text-xs sm:text-sm text-center text-white">
              ผู้รอดชีวิต
            </h2>
          </div>
        </div>
        <div v-else class="flex items-center justify-center bg-gray-900 bg-opacity-80 rounded-xl shadow-2xl h-full survivor-bg">
          <i class="fas fa-spinner text-2xl text-white animate-spin"></i>
        </div>

        <!-- ปุ่มเมนูทั่วไป -->
        <router-link :to="{ name: 'HowToPlay' }" class="button-wrapper">
          <div class="btn btn-secondary">
            <div class="btn-content">
              <i class="fas fa-file-alt text-lg"></i>
              <span>วิธีการเล่น</span>
            </div>
          </div>
        </router-link>
        <router-link :to="{ name: 'Map' }" class="button-wrapper">
          <div class="btn btn-secondary">
            <div class="btn-content">
              <i class="fas fa-map text-lg"></i>
              <span>แผนที่</span>
            </div>
          </div>
        </router-link>
        <router-link :to="{ name: 'Zombie' }" class="button-wrapper">
          <div class="btn btn-secondary">
            <div class="btn-content">
              <i class="fas fa-head-side-virus text-lg"></i>
              <span>ซอมบี้</span>
            </div>
          </div>
        </router-link>
        <router-link :to="{ name: 'CriminalList' }" class="button-wrapper">
          <div class="btn btn-secondary">
            <div class="btn-content">
              <i class="fas fa-mask text-lg"></i>
              <span>อาชญากร</span>
            </div>
          </div>
        </router-link>
        <router-link :to="{ name: 'Item' }" class="button-wrapper">
          <div class="btn btn-secondary">
            <div class="btn-content">
              <i class="fas fa-helmet-safety text-lg"></i>
              <span>ไอเทม</span>
            </div>
          </div>
        </router-link>
        <router-link :to="{ name: 'Ghost' }" class="button-wrapper">
          <div class="btn btn-secondary"> 
            <div class="btn-content">
              <i class="fas fa-ghost text-lg"></i>
              <span>วิญญาณ</span>
            </div>
          </div>
        </router-link>
        <router-link :to="{ name: 'Merchant' }" class="button-wrapper">
          <div class="btn btn-secondary">
            <div class="btn-content">
              <i class="fas fa-sack-dollar text-lg"></i>
              <span>พ่อค้า</span>
            </div>
          </div>
        </router-link>
        <router-link  :to="{ name: 'Villager' }" class="button-wrapper">
          <div class="btn btn-secondary">
            <div class="btn-content">
              <i class="fas fa-people-pulling text-lg"></i>
              <span>ชาวบ้าน</span>
            </div>
          </div>
        </router-link>
         <router-link  :to="{ name: 'Mutant' }" class="button-wrapper">
          <div class="btn btn-secondary">
            <div class="btn-content">
              <i class="fas fa-paw text-lg"></i>
              <span>สิ่งมีชีวิตกลายพันธุ์</span>
            </div>
          </div>
        </router-link>
        <a href="https://discord.gg/UA9pNFWwCe" class="button-wrapper">
          <div class="btn btn-secondary">
            <div class="btn-content">
              <i class="fab fa-discord text-lg"></i>
              <span>ดิสคอร์ด</span>
            </div>
          </div>
        </a>

        <router-link v-if="user" :to="{ name: 'Posts' }" class="button-wrapper">
          <div class="btn btn-primary">
            <div class="btn-content relative">
              <i class="fas fa-comments text-lg"></i>
              <span>พูดคุย</span>
            </div>
          </div>
        </router-link>

        <!-- ปุ่มแจ้งเตือน -->
        <router-link v-if="user" :to="{ name: 'Notifications' }" class="button-wrapper">
          <div class="btn btn-primary">
            <div class="btn-content relative">
              <i class="fas fa-bell text-lg"></i>
              <span>แจ้งเตือน</span>
              <span
                v-if="unreadNotifications > 0"
                class="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
              >
                {{ unreadNotifications }}
              </span>
            </div>
          </div>
        </router-link>

        <!-- ปุ่มเติมเงิน -->
        <router-link v-if="user" :to="{ name: 'Topup' }" class="button-wrapper" style="position: relative;">
          <div class="btn btn-primary relative">
            <div class="btn-content">
              <i class="fas fa-money-bills text-lg"></i>
              <span>เติมเงิน</span>
            </div>
            <!-- Admin Status Indicator -->
            <span
              class="absolute top-1 right-1 w-3 h-3 rounded-full"
              :class="online ? 'bg-green-500' : 'bg-yellow-500'"
              :title="online ? 'ทีมงานออนไลน์' : 'ทีมงานออฟไลน์'"
            ></span>
          </div>
          <!-- ✨ Bonus Badge (outside .btn to avoid overflow:hidden clipping) -->
          <span
            v-if="topupBonusActive"
            style="position: absolute; top: -10px; left: 50%; transform: translateX(-50%); z-index: 10;"
            class="bg-green-500 text-white font-bold rounded-full px-2 py-1 text-xs shadow-lg border border-green-300 pointer-events-none"
            :title="`โบนัสเติมเงิน +${topupBonusPercentage}% วันนี้!`"
          >
            +{{ topupBonusPercentage }}%
          </span>
        </router-link>

        <!-- ปุ่มบัญชี -->
        <router-link v-if="user" :to="{ name: 'Account' }" class="button-wrapper">
          <div class="btn btn-primary">
            <div class="btn-content">
              <i class="fas fa-user-circle text-lg"></i>
              <span>บัญชี</span>
            </div>
          </div>
        </router-link>

        <!-- กลุ่มปุ่ม Admin -->
         <router-link v-if="user && isAdmin" :to="{ name: 'Gank' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-user-circle text-lg"></i>
              <span>แก๊งค์</span>
            </div>
          </div>
        </router-link>
        <router-link v-if="user && isAdmin" :to="{ name: 'MarketplaceHistory' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-user-circle text-lg"></i>
              <span>ประวัติตลาด</span>
            </div>
          </div>
        </router-link>
         <router-link v-if="user && isAdmin" :to="{ name: 'OutpostSetting' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-sign text-lg"></i>
              <span>Outpost Setting</span>
            </div>
          </div>
        </router-link>
        <router-link v-if="user && isAdmin" :to="{ name: 'ManageRobot' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-user-circle text-lg"></i>
              <span>จัดการหุ่นยนต์</span>
            </div>
          </div>
        </router-link>
        <router-link v-if="user && isAdmin" :to="{ name: 'SurvivorCharacter' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-user-circle text-lg"></i>
              <span>ตัวละครทั้งหมด</span>
            </div>
          </div>
        </router-link>
        <router-link v-if="user && isAdmin" :to="{ name: 'LogAdmin' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-user-circle text-lg"></i>
              <span>ประวัติทั้งหมด</span>
            </div>
          </div>
        </router-link>
        <router-link v-if="user && isAdmin" :to="{ name: 'TopupList' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-user-circle text-lg"></i>
              <span>รายการเติมเงิน</span>
            </div>
          </div>
        </router-link>
        <router-link v-if="user && isAdmin" :to="{ name: 'Users' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-user-circle text-lg"></i>
              <span>สมาชิก</span>
            </div>
          </div>
        </router-link>
        <router-link v-if="user && isAdmin" :to="{ name: 'ItemTransferHistory' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-exchange text-lg"></i>
              <span>ประวัติการโอนไอเทม</span>
            </div>
          </div>
        </router-link>
        <router-link v-if="user && isAdmin" :to="{ name: 'AssassinateSurvivor' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-exchange text-lg"></i>
              <span>ประวัติลอบสังหาร</span>
            </div>
          </div>
        </router-link>
        <router-link v-if="user && isAdmin" :to="{ name: 'DailyStats' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-signal"></i>
              <span>สถิติรายวัน</span>
            </div>
          </div>
        </router-link>
        <router-link v-if="user && isAdmin" :to="{ name: 'Stats' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-signal"></i>
              <span>สถิติทุกวัน</span>
            </div>
          </div>
        </router-link>
        <router-link v-if="user && isAdmin" :to="{ name: 'CheckCharacter' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-magnifying-glass"></i>
              <span>ตรวจสอบผู้เล่น</span>
            </div>
          </div>
        </router-link>
        <router-link v-if="user && isAdmin" :to="{ name: 'CheckCamp' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-tent"></i>
              <span>ตรวจสอบแคมป์</span>
            </div>
          </div>
        </router-link>
        <router-link v-if="user && isAdmin" :to="{ name: 'CheckStorageAndBag' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-magnifying-glass"></i>
              <span>ตรวจสอบคลังและกระเป๋า</span>
            </div>
          </div>
        </router-link>
        <router-link v-if="user && isAdmin" :to="{ name: 'AdminMigratePlayers' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-magic"></i>
              <span>Migrate ผู้เล่น</span>
            </div>
          </div>
        </router-link>
        <router-link v-if="user && isAdmin" :to="{ name: 'Setting' }" class="button-wrapper">
          <div class="btn btn-admin">
            <div class="btn-content">
              <i class="fas fa-gear"></i>
              <span>ตั้งค่า</span>
            </div>
          </div>
        </router-link>
      </div>

      <div v-else class="flex items-center justify-center h-full">
        <i class="fas fa-spinner text-3xl text-white animate-spin"></i>
      </div>

      <!-- Manage Modal (owner only) -->
      <ManageOutpostModal
        v-if="manageOpen"
        :outpost-id="activeOutpostId"
        @close="closeManage"
      />
      
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, onUnmounted, computed } from 'vue';
import { projectAuth, projectFirestore, projectFunctions } from '../firebase/config';
import firebase from 'firebase/app';
import getUser from '../composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { useInventoryStore } from '@/composables/useInventoryStore';
import { useMissionStatsStore } from '@/composables/useMissionStatsStore';
import ManageOutpostModal from '@/components/ManageOutpostModal.vue';

export default {
  name: 'Home',
  components: { ManageOutpostModal },
  setup() {
    const error = ref(null);
    const isLoading = ref(false);
    const isInitialLoading = ref(true);
    const hasCharacter = ref(false);
    const isAdmin = ref(false);
    const playerStore = usePlayerStore();
    const inventoryStore = useInventoryStore();
    const missionStatsStore = useMissionStatsStore();
    const characterLevel = ref(0);
    const dismissedGameGuide = ref(true);
    const dismissedDeathNotification = ref(true);
    const zombiesKilled = ref(0);
    const death = ref(0);
    const firstTimeSell = ref(false);
    const { user } = getUser();
    const activePlayer = ref(0);
    const maxPlayer = ref(0);
    const canCreateCharacter = ref(false);
    const survivorError = ref(null);
    const isLoadingSettings = ref(true);
    const unreadNotifications = ref(0);
    const mysteriousStorageCount = ref(0);
    const freeBoxCount = ref(0);
    const online = ref(false);
    const serverStatusError = ref(null);
    const weeklyDepositedDiamond = ref(0);
    const activeShops = ref(0);

    // Occupied Maps
    const occupiedMaps = ref([]);
    const occupiedOutposts = ref([]);
    const isOpeningTreasure = ref(false);
    const treasureMessage = ref('');

    // Countdown states
    const protectAssassinateRemaining = ref(0);
    const insuranceRemainingSeconds = ref(0);
    const currentTime = ref(Date.now());

    // ✨ Topup Bonus state
    const topupBonusPercentage = ref(0);
    const topupBonusEndTime = ref(null);

    const topupBonusActive = computed(() => {
      return topupBonusPercentage.value > 0 && topupBonusEndTime.value && new Date() < topupBonusEndTime.value;
    });

    // Modal control: Manage (owner)
    const manageOpen = ref(false);
    const activeOutpostId = ref(null);
    const openManage = (id) => {
      activeOutpostId.value = id;
      manageOpen.value = true;
    };
    const closeManage = () => {
      manageOpen.value = false;
      activeOutpostId.value = null;
    };

    let unsubscribeNotifications = null;
    let unsubscribeSettings = null;
    let unsubscribeBonus = null; // ✨ bonus listener
    let protectInterval = null;
    let timeUpdateInterval = null;

    watch(() => playerStore.characterData, (data) => {
      characterLevel.value = data?.level || 0;
    }, { immediate: true });

    watch(() => playerStore.userData, (data) => {
      if (data) {
        hasCharacter.value = data.characterCreated === true;
        isAdmin.value = data.admin === true;
        dismissedGameGuide.value = data.dismissedGameGuide || false;
        dismissedDeathNotification.value = data.dismissedDeathNotification || false;
        isInitialLoading.value = false;
      } else {
        hasCharacter.value = false;
        isAdmin.value = false;
        dismissedGameGuide.value = false;
        dismissedDeathNotification.value = false;
        isInitialLoading.value = false;
      }
    }, { immediate: true });

    watch(() => playerStore.characterPrivateData, (data) => {
      if (data) {
        freeBoxCount.value = data.openFreeMysteriousBox || 0;
        if (data.protectAssassinate) {
          const protectUntilMs = data.protectAssassinate.toMillis ? data.protectAssassinate.toMillis() : data.protectAssassinate;
          const now = Date.now();
          protectAssassinateRemaining.value = Math.max(0, Math.floor((protectUntilMs - now) / 1000));
        } else {
          protectAssassinateRemaining.value = 0;
        }
        if (data.insuranceExpiry) {
          const insuranceMs = data.insuranceExpiry.toMillis ? data.insuranceExpiry.toMillis() : data.insuranceExpiry;
          const now = Date.now();
          insuranceRemainingSeconds.value = Math.max(0, Math.floor((insuranceMs - now) / 1000));
        } else {
          insuranceRemainingSeconds.value = 0;
        }
      } else {
        freeBoxCount.value = 0;
        protectAssassinateRemaining.value = 0;
        insuranceRemainingSeconds.value = 0;
      }
    }, { immediate: true });

    const lockedButtons = [
      { name: 'Character', route: 'Character', label: 'ตัวละคร', icon: 'fas fa-person' },
      { name: 'Storage', route: 'Storage', label: 'ห้องเก็บของ', icon: 'fas fa-warehouse' },
      { name: 'Camp', route: 'Camp', label: 'แคมป์', icon: 'fas fa-tent' },
      { name: 'Shop', route: 'Shop', label: 'ร้านค้า', icon: 'fas fa-store' },
      { name: 'Craft', route: 'Craft', label: 'คราฟ', icon: 'fas fa-hammer' },
      { name: 'ManagerRoom', route: 'ManagerRoom', label: 'ผู้จัดการ', icon: 'fas fa-user-tie' },
      { name: 'Tattoo', route: 'Tattoo', label: 'ร้านช่างสัก', icon: 'fas fa-dragon' },
      { name: 'Criminal', route: 'Criminal', label: 'ประกาศจับ', icon: 'fas fa-user-secret' },
      { name: 'Survivor', route: 'Survivor', label: 'ผู้รอดชีวิต', icon: 'fas fa-users' },
      { name: 'Marketplace', route: 'Marketplace', label: 'ตลาด', icon: 'fas fa-users-line' },
      { name: 'Outpost', route: 'Outpost', label: 'เอาท์โพสต์', icon: 'fas fa-tower-observation' },
      { name: 'Occupier', route: 'Occupier', label: 'ผู้ยึดครอง', icon: 'fas fa-flag' },
      { name: 'SurvivalLeader', route: 'SurvivalLeader', label: 'อันดับ', icon: 'fas fa-medal' },
      { name: 'MysteriousStorage', route: 'MysteriousStorage', label: 'คลังปริศนา', icon: 'fas fa-question' },
      { name: 'Log', route: 'Log', label: 'ประวัติ', icon: 'fas fa-clock-rotate-left' },
    ];

    const activePlayerStatus = computed(() => {
      if (activePlayer.value <= 150) return 'ปกติ';
      if (activePlayer.value <= 300) return 'หนาแน่น';
      return 'หนาแน่นมาก';
    });

    const activePlayerColor = computed(() => {
      if (activePlayer.value <= 150) return '#00FF00';
      if (activePlayer.value <= 300) return '#FFFF00';
      return '#FF0000';
    });

    const showSellItemNotification = computed(() => !firstTimeSell.value);

    const insuranceHoursMinutes = computed(() => {
      const total = Math.max(0, insuranceRemainingSeconds.value);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      return `${hh}:${mm}`;
    });

    const fetchServerStatus = async () => {
      try {
        const doc = await projectFirestore.collection('server').doc('admin').get();
        online.value = doc.exists ? (doc.data().online || false) : false;
        if (!doc.exists) serverStatusError.value = 'ไม่พบข้อมูลสถานะทีมงาน';
      } catch (err) {
        serverStatusError.value = `เกิดข้อผิดพลาดในการตรวจสอบสถานะ: ${err.message}`;
        online.value = false;
      }
    };

    // ✨ Fetch bonus info realtime
    const fetchBonusInfo = () => {
      unsubscribeBonus = projectFirestore.collection('server').doc('bonus').onSnapshot(
        (doc) => {
          if (doc.exists) {
            const data = doc.data();
            topupBonusPercentage.value = data.bonusPercentage || 0;
            topupBonusEndTime.value = data.bonusEndTime?.toDate() || null;
          } else {
            topupBonusPercentage.value = 0;
            topupBonusEndTime.value = null;
          }
        },
        (err) => {
          console.error('Error fetching bonus info:', err);
          topupBonusPercentage.value = 0;
          topupBonusEndTime.value = null;
        }
      );
    };

    const loadServerSettings = () => {
      isLoadingSettings.value = true;
      survivorError.value = null;
      unsubscribeSettings = projectFirestore.collection('server').doc('setting').onSnapshot(
        (doc) => {
          if (doc.exists) {
            const data = doc.data();
            activePlayer.value = data.activePlayer || 0;
            maxPlayer.value = data.maxPlayer || 0;
            canCreateCharacter.value = data.canCreateCharacter || false;
            activeShops.value = data.activeShops || 0;
          } else {
            survivorError.value = 'ไม่พบข้อมูลการตั้งค่าเซิร์ฟเวอร์';
            activeShops.value = 0;
          }
          isLoadingSettings.value = false;
        },
        (err) => {
          console.error('Error fetching server settings:', err);
          survivorError.value = 'ไม่สามารถโหลดข้อมูลจำนวนผู้รอดชีวิตได้';
          activeShops.value = 0;
          isLoadingSettings.value = false;
        }
      );
    };

    watch(() => missionStatsStore.characterStatsData, (data) => {
      if (data) {
        zombiesKilled.value = data.zombiesKilled || 0;
        death.value = data.death || 0;
      } else {
        zombiesKilled.value = 0;
        death.value = 0;
      }
    }, { immediate: true });

    watch(() => playerStore.userData, (data) => {
      firstTimeSell.value = data?.firstTimeSell === true;
    }, { immediate: true });

    watch(() => inventoryStore.storageData, (data) => {
      weeklyDepositedDiamond.value = data?.weeklyDepositedDiamond || 0;
    }, { immediate: true });

    const fetchOccupiedMaps = async () => {
      if (!user.value?.uid) {
        occupiedMaps.value = [];
        return;
      }
      try {
        const snapshot = await projectFirestore
          .collection('maps')
          .where('occupierUid', '==', user.value.uid)
          .get();
        occupiedMaps.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (err) {
        console.error('Error fetching occupied maps:', err);
        occupiedMaps.value = [];
      }
    };

    const fetchOccupiedOutposts = async () => {
      if (!user.value?.uid) {
        occupiedOutposts.value = [];
        return;
      }
      try {
        const snapshot = await projectFirestore
          .collection('outposts')
          .where('ownerUid', '==', user.value.uid)
          .get();
        occupiedOutposts.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (err) {
        console.error('Error fetching occupied outposts:', err);
        occupiedOutposts.value = [];
      }
    };

    const isMapInCooldown = (map) => {
      return map.treasureAvailableAt && currentTime.value < map.treasureAvailableAt.toMillis();
    };

    const isOutpostInCooldown = (outpost) => {
      return outpost.treasureAvailableAt && currentTime.value < outpost.treasureAvailableAt.toMillis();
    };

    const getMapCooldownText = (map) => {
      if (!map.treasureAvailableAt) return '';
      const diff = map.treasureAvailableAt.toMillis() - currentTime.value;
      if (diff <= 0) return '';
      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      return `${hours}h ${minutes}m ${seconds}s`;
    };

    const getOutpostCooldownText = (outpost) => {
      if (!outpost.treasureAvailableAt) return '';
      const diff = outpost.treasureAvailableAt.toMillis() - currentTime.value;
      if (diff <= 0) return '';
      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      return `${hours}h ${minutes}m ${seconds}s`;
    };

    const getMapButtonClasses = (map) => {
      const isOpened = map.treasureOpened;
      const isCooldown = isMapInCooldown(map);

      if (isOpened) {
        return 'bg-red-900 border-red-800 bg-opacity-70 text-gray-300 cursor-not-allowed';
      } else if (isCooldown) {
        return 'bg-yellow-900 border-yellow-800 bg-opacity-70 text-gray-300 cursor-not-allowed';
      } else {
        return 'bg-green-600 bg-opacity-70 hover:bg-green-900 text-white border-green-500';
      }
    };

    const getOutpostButtonClasses = (outpost) => {
      const isOpened = outpost.treasureOpened;
      const isCooldown = isOutpostInCooldown(outpost);

      if (isOpened) {
        return 'bg-red-900 border-red-800 bg-opacity-70 text-gray-300 cursor-not-allowed';
      } else if (isCooldown) {
        return 'bg-yellow-900 border-yellow-800 bg-opacity-70 text-gray-300 cursor-not-allowed';
      } else {
        return 'bg-green-600 bg-opacity-70 hover:bg-green-900 text-white border-green-500';
      }
    };

    const openTreasure = async (map) => {
      if (!map.name) {
        treasureMessage.value = 'ไม่สามารถเปิดกล่องสมบัติได้: ไม่พบชื่อแผนที่';
        return;
      }
      isOpeningTreasure.value = true;
      treasureMessage.value = '';
      try {
        const minDelay = new Promise(resolve => setTimeout(resolve, 2500));
        const [response] = await Promise.all([
          projectFunctions.httpsCallable('gameActions')({
            action: 'occupierActions',
            subAction: 'openTreasureBox',
            mapName: map.name
          }),
          minDelay
        ]);
        treasureMessage.value = response.data.message;
        await fetchOccupiedMaps();
      } catch (error) {
        console.error('Error opening treasure box:', error);
        treasureMessage.value = `ไม่สามารถเปิดกล่องสมบัติได้: ${error.message}`;
      } finally {
        isOpeningTreasure.value = false;
      }
    };

    const openOutpostTreasure = async (outpost) => {
      if (!outpost.id) {
        treasureMessage.value = 'ไม่สามารถเปิดกล่องสมบัติได้: ไม่พบ ID outpost';
        return;
      }
      isOpeningTreasure.value = true;
      treasureMessage.value = '';
      try {
        const minDelay = new Promise(resolve => setTimeout(resolve, 2500));
        const [response] = await Promise.all([
          projectFunctions.httpsCallable('gameActions')({
            action: 'outpostActions',
            subAction: 'openTreasureBox',
            outpostId: outpost.id
          }),
          minDelay
        ]);
        treasureMessage.value = response.data.message;
        await fetchOccupiedOutposts();
      } catch (error) {
        console.error('Error opening outpost treasure box:', error);
        treasureMessage.value = `ไม่สามารถเปิดกล่องสมบัติได้: ${error.message}`;
      } finally {
        isOpeningTreasure.value = false;
      }
    };

    const dismissGameGuide = async () => {
      if (!user.value || !user.value.uid) return;
      try {
        const dismissFunction = projectFunctions.httpsCallable('gameActions');
        await dismissFunction({ action: 'characterActions', subAction: 'dismissGameGuide' });
        dismissedGameGuide.value = true;
      } catch (err) {
        console.error('Error dismissing game guide:', err);
        error.value = 'ไม่สามารถปิดการแจ้งเตือนคำแนะนำได้';
      }
    };

    const dismissDeathNotification = async () => {
      if (!user.value || !user.value.uid) return;
      try {
        const dismissFunction = projectFunctions.httpsCallable('gameActions');
        await dismissFunction({ action: 'characterActions', subAction: 'dismissDeathNotification' });
        dismissedDeathNotification.value = true;
      } catch (err) {
        console.error('Error dismissing death notification:', err);
        error.value = 'ไม่สามารถปิดการแจ้งเตือนการตายได้';
      }
    };

    const fetchUnreadNotifications = () => {
      if (unsubscribeNotifications) {
        unsubscribeNotifications();
        unsubscribeNotifications = null;
      }

      if (!user.value) {
        unreadNotifications.value = 0;
        return;
      }

      unsubscribeNotifications = projectFirestore
        .collection('notifications')
        .where('userId', '==', user.value.uid)
        .where('read', '==', false)
        .limit(5)
        .onSnapshot(
          (snapshot) => {
            unreadNotifications.value = snapshot.size;
          },
          (err) => {
            console.error('Error fetching unread notifications:', err);
          }
        );
    };

    watch(() => inventoryStore.mysteriousStorageData, (data) => {
      const v2Count = data?.items ? Object.keys(data.items).length : 0;
      const v1Count = Array.isArray(data?.inventory) ? data.inventory.length : 0;
      mysteriousStorageCount.value = v2Count + v1Count;
    }, { immediate: true });

    const checkCharacterAndAdmin = () => {
      // userData state is now managed by watch(() => playerStore.userData) above
    };

    const updateCountdowns = () => {
      if (protectAssassinateRemaining.value > 0) protectAssassinateRemaining.value -= 1;
      if (insuranceRemainingSeconds.value > 0) insuranceRemainingSeconds.value -= 1;
    };

    const updateCurrentTime = () => {
      currentTime.value = Date.now();
    };

    let debounceTimer;
    onMounted(() => {
      loadServerSettings();
      fetchUnreadNotifications();
      fetchServerStatus();
      fetchOccupiedMaps();
      fetchOccupiedOutposts();
      fetchBonusInfo(); // ✨

      protectInterval = setInterval(updateCountdowns, 1000);
      timeUpdateInterval = setInterval(updateCurrentTime, 1000);
    });

    watch(user, () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(async () => {
        fetchUnreadNotifications();
        fetchServerStatus();
        fetchOccupiedMaps();
        fetchOccupiedOutposts();
      }, 500);
    });

    onUnmounted(() => {
      if (unsubscribeSettings) unsubscribeSettings();
      if (unsubscribeNotifications) {
        unsubscribeNotifications();
        unsubscribeNotifications = null;
      }
      if (unsubscribeBonus) unsubscribeBonus(); // ✨
      if (protectInterval) clearInterval(protectInterval);
      if (timeUpdateInterval) clearInterval(timeUpdateInterval);
    });

    const signInWithGoogle = async () => {
      isLoading.value = true;
      try {
        const provider = new firebase.auth.GoogleAuthProvider();
        provider.addScope('profile email');
        const result = await projectAuth.signInWithPopup(provider);
        user.value = result.user;
        error.value = null;
      } catch (err) {
        console.error('Error signing in with Google:', err);
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
      };



    return {
      user,
      error,
      isLoading,
      isInitialLoading,
      hasCharacter,
      isAdmin,
      characterLevel,
      dismissedGameGuide,
      dismissedDeathNotification,
      zombiesKilled,
      death,
      lockedButtons,
      activePlayer,
      maxPlayer,
      canCreateCharacter,
      survivorError,
      isLoadingSettings,
      unreadNotifications,
      mysteriousStorageCount,
      freeBoxCount,
      online,
      serverStatusError,
      weeklyDepositedDiamond,
      protectAssassinateRemaining,
      insuranceRemainingSeconds,
      activeShops,
      insuranceHoursMinutes,
      occupiedMaps,
      occupiedOutposts,
      isOpeningTreasure,
      treasureMessage,
      signInWithGoogle,
      activePlayerStatus,
      activePlayerColor,
      showSellItemNotification,
      dismissGameGuide,
      dismissDeathNotification,
      openTreasure,
      openOutpostTreasure,
      isMapInCooldown,
      isOutpostInCooldown,
      getMapCooldownText,
      getOutpostCooldownText,
      getMapButtonClasses,
      getOutpostButtonClasses,
      // ✨ bonus
      topupBonusActive,
      topupBonusPercentage,
      // manage modal
      manageOpen,
      activeOutpostId,
      openManage,
      closeManage,
    };
  },
};
</script>

<style scoped>
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fcondo.png?alt=media&token=198ebb1c-66e5-4bf0-aa79-32bff287aa09') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
}

.survivor-bg {
  background-image: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/images%2Fzombiepattern.png?alt=media&token=4fd62a5a-2f5b-43b4-9b45-40473ef90c52');
  background-size: cover;
  background-position: center;
  position: relative;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.5);
}

.survivor-bg::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(17, 24, 39, 0.6);
  border-radius: 0.75rem;
  z-index: 0;
}

.survivor-bg > * {
  position: relative;
  z-index: 1;
}

.character-bg {
  background-image: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/images%2Fzombiepattern.png?alt=media&token=4fd62a5a-2f5b-43b4-9b45-40473ef90c52');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.5);
}

.character-bg::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(17, 24, 39, 0.6);
  border-radius: 0.75rem;
  z-index: 0;
}

.character-bg > * {
  position: relative;
  z-index: 1;
}

.button-wrapper { position: relative; perspective: 1000px; }

.btn {
  position: relative;
  border: none;
  border-radius: 0.75rem;
  padding: 0.75rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Orbitron', sans-serif;
  text-decoration: none;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  background-size: 200% 100%;
}

.btn-content { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; position: relative; z-index: 1; }

.btn::before {
  content: '';
  position: absolute;
  top: 0; left: -100%; width: 100%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
  z-index: 0;
}

.btn:hover::before { left: 100%; }

.btn-primary {
  background-color: rgba(17, 24, 39, 0.7);
  border: 1px solid rgb(31, 41, 55);
  border-radius: 0.5rem;
}

.btn-primary:hover {
  background-color: rgba(17, 24, 39, 1);
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 8px 25px rgba(17, 24, 39, 0.5);
  background-position: right center;
}

.btn-primary:active { transform: translateY(0); box-shadow: 0 4px 15px rgba(17, 24, 39, 0.3); }

.btn-google {
  background-color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgb(31, 41, 55);
  border-radius: 0.5rem; color: black;
}

.btn-google:hover {
  background-color: rgba(255, 255, 255, 1);
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 8px 25px rgba(255, 255, 255, 1);
  background-position: right center;
}

.btn-google:active { transform: translateY(0); box-shadow: 0 4px 15px rgba(255, 255, 255, 0.3); }

.btn-secondary {
  background-color: rgba(107, 114, 128, 0.7);
  border: 1px solid rgb(31, 41, 55);
  border-radius: 0.5rem;
}

.btn-secondary:hover {
  background-color: rgba(107, 114, 128, 1);
  box-shadow: 0 8px 25px rgba(107, 114, 128, 0.5);
  transform: translateY(-3px) scale(1.05);
  background-position: right center;
}

.btn-secondary:active { transform: translateY(0); box-shadow: 0 4px 15px rgba(107, 114, 128, 0.3); }

.btn-locked {
  background-color: rgba(75, 85, 99, 0.5);
  border: 1px solid rgb(31, 41, 55);
  border-radius: 0.5rem;
  cursor: not-allowed;
  opacity: 0.7;
}

.btn-locked:hover { transform: none; box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3); }

.btn-danger { background: linear-gradient(90deg, rgba(127, 29, 29, 0.8), rgba(153, 27, 27, 0.8)); }
.btn-danger:hover {
  background: linear-gradient(90deg, rgba(127, 29, 29, 1), rgba(153, 27, 27, 1));
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 8px 25px rgba(153, 27, 27, 0.5);
  background-position: right center;
}
.btn-danger:active { transform: translateY(0); box-shadow: 0 4px 15px rgba(153, 27, 27, 0.3); }

.btn-admin {
  background: linear-gradient(90deg, rgba(185, 28, 28, 0.8), rgba(220, 38, 38, 0.8));
  border: 2px solid #facc15;
  position: relative;
}
.btn-admin:hover {
  background: linear-gradient(90deg, rgba(185, 28, 28, 1), rgba(220, 38, 38, 1));
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 8px 25px rgba(220, 38, 38, 0.5), 0 0 10px #facc15;
  background-position: right center;
}
.btn-admin:active { transform: translateY(0); box-shadow: 0 4px 15px rgba(220, 38, 38, 0.3); }
.btn-admin:after {
  content: 'ADMIN';
  position: absolute;
  top: -10px; right: -10px;
  background: #facc15; color: #1f2937;
  font-size: 0.6rem; padding: 2px 6px; border-radius: 4px; font-weight: bold;
}

.btn:disabled {
  background: linear-gradient(90deg, rgba(75, 85, 99, 0.6), rgba(107, 114, 128, 0.6));
  transform: none;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  cursor: not-allowed;
}

.btn i { font-size: 1.25rem; transition: transform 0.3s ease; }
.btn span { font-size: 0.875rem; letter-spacing: 0.5px; text-transform: uppercase; font-weight: 600; }
.btn:hover i { transform: scale(1.2) rotate(5deg); }

.manage-icon {
  cursor: pointer;
}

@media (max-width: 639px) {
  .btn span { font-size: 0.75rem; }
  .btn { padding: 0.5rem; }
  .btn i { font-size: 1rem; }
}
@media (min-width: 640px) { .btn { padding: 1rem; } }
.font-orbitron { font-family: 'Orbitron', sans-serif; }
</style>
